import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { MESSAGE } from "../../utils/validationMessage";
import { PASSWORDS_REGEX } from "../../utils/constants";
import errorHandler from "../../utils/errorHandler";
import {
  IconButton,
  InputAdornment,
  Link as MLink,
  Stack,
  TextField,
  Typography,
  CardContent,
} from "@mui/material";
import palette from "../../theme/palette";
import Iconify from "../../components/iconify";
import { adminAuthServices } from "../../services/authService";

const validationSchema = Yup.object().shape({
  otp: Yup.string()
    .required("OTP is required")
    .matches(/^[0-9]{4}$/, "OTP must be 4 digits"),
  password: Yup.string()
    .required(MESSAGE.REQUIRED)
    .matches(PASSWORDS_REGEX, MESSAGE.PASSWORD),
  cpassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required(MESSAGE.REQUIRED),
});

export default function ResetPassword() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: { otp: "", password: "", cpassword: "" },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const res = await adminAuthServices.resetPassword({
          email: state?.email,
          otp: values.otp,
          newPassword: values.password,
        });

        if (res?.data?.status) {
          toast.success(res?.data?.message || "Password reset successful");
          navigate("/login");
        }
      } catch (err) {
        errorHandler(err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Typography variant="h3" sx={{ mb: 2 }} align="center">
        Reset Password
      </Typography>
      <CardContent>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              name="email"
              label="Email"
              value={state?.email || ""}
              disabled
              fullWidth
            />

            <TextField
              name="otp"
              label="Enter OTP"
              placeholder="4 digit code"
              inputProps={{ maxLength: 4, pattern: "[0-9]*" }}
              {...getFieldProps("otp")}
              error={Boolean(touched.otp && errors.otp)}
              helperText={touched.otp && errors.otp}
              fullWidth
            />

            {/* ✅ New Password */}
            <TextField
              name="password"
              label="Enter New Password"
              placeholder="Enter New Password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      <Iconify
                        icon={
                          showPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                        }
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                onCopy: (e) => e.preventDefault(), // prevent copy
              }}
              {...getFieldProps("password")}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
              fullWidth
            />

            {/* ✅ Confirm Password */}
            <TextField
              name="cpassword"
              label="Confirm New Password"
              placeholder="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      <Iconify
                        icon={
                          showConfirmPassword
                            ? "eva:eye-fill"
                            : "eva:eye-off-fill"
                        }
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...getFieldProps("cpassword")}
              error={Boolean(touched.cpassword && errors.cpassword)}
              helperText={touched.cpassword && errors.cpassword}
              fullWidth
            />
          </Stack>

          {/* ✅ Submit Button */}
          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{ my: 2 }}
          >
            Change Password
          </LoadingButton>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ my: 2 }}
          >
            <MLink
              variant="subtitle2"
              underline="hover"
              to="/login"
              as={Link}
              color={palette.primary.main}
            >
              Back to Login
            </MLink>
          </Stack>
        </Form>
      </CardContent>
    </FormikProvider>
  );
}
